import firebase from "firebase/app";
import uniqid from "uniqid";

import { Checkbox, Header, Input, Modal } from "semantic-ui-react";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import articleTypes from "astrid-firestore/src/api/articles/constants/articleTypes";
import artifactMarkerTypes from "astrid-firestore/src/api/artifacts/constants/artifactMarkerTypes";
import createArtifact from "astrid-firestore/src/api/artifacts/createArtifact";
import getAcceptedArtifactFiles from "astrid-firestore/src/api/artifacts/getAcceptedArtifactFiles";
import sortArtifactFilesByName from "astrid-firestore/src/api/artifacts/sortArtifactFilesByName";

import Button from "../../../ui/components/Buttons/Button";
import FileDropzone from "../../../ui/components/FileUpload/FileDropzone";
import FileUploadTable from "../../../ui/components/FileUpload/FileUploadTable";
import useLoadingFunction from "../../../ui/hooks/useLoadingFunction";

import { useProfile } from "../../../authentication/state/profile";

import useArtifactFilesUpload from "../../hooks/useArtifactFilesUpload";

export default function ArtifactFilesUploadForm({
	work,
	article,
	producer,
	publisher,
	production,
	defaultName = "",
	defaultFiles = [],
	artifactId,
	onSuccess,
	onClose,
}) {
	const user = useProfile();

	const { t } = useTranslation();

	const [name, setName] = useState(defaultName);
	const [files, setFiles] = useState(defaultFiles);
	const [fileMarkers, setFileMarkers] = useState(true);

	const { uploadArtifactFiles, progress } = useArtifactFilesUpload();

	const [onClickUpload, loading, error] = useLoadingFunction(async () => {
		const { id } = firebase.firestore().collection("artifacts").doc();

		const artifactFiles = await uploadArtifactFiles(artifactId || id, files);
		const sortedArtifactFiles = sortArtifactFilesByName(artifactFiles);

		if (!artifactId) {
			await createArtifact(
				firebase,
				{
					id,
					name,
					createdBy: user,
					work: work || article?.work || null,
					article: article || null,
					producer: producer || null,
					publisher: publisher || null,
					production: production || null,
					markers: fileMarkers
						? sortedArtifactFiles.map((file) => ({
								id: uniqid(),
								type: artifactMarkerTypes.CHAPTER,
								text: "",
								fileId: file.id,
								position: 0,
						  }))
						: [],
				},
				sortedArtifactFiles,
			);
		}

		onSuccess?.({ name, files: sortedArtifactFiles, fileMarkers });
		onClose();
	});

	return (
		<Modal open size="tiny" onClose={onClose} closeOnDimmerClick>
			<Modal.Header>{t("uploadFiles", "Upload files")}</Modal.Header>

			<Modal.Content scrolling>
				{defaultName && (
					<>
						<Header as="h5" content={t("name", "Name")} style={{ marginBottom: 5 }} />
						<Input value={name} onChange={(e) => setName(e.target.value)} fluid />
					</>
				)}

				{files.length > 0 ? (
					<FileUploadTable files={files} progress={progress} style={{ marginTop: 10 }} />
				) : (
					<FileDropzone accept={getAcceptedArtifactFiles(article)} onChange={(files) => setFiles(files)} />
				)}

				{article?.type !== articleTypes.EBOOK && (
					<Checkbox
						label={t("setFileChapters", "Set file chapters")}
						checked={fileMarkers}
						onChange={() => setFileMarkers((fileMarkers) => !fileMarkers)}
					/>
				)}
			</Modal.Content>

			<Modal.Actions>
				<Button disabled={loading} onClick={onClose}>
					{t("cancel", "Cancel")}
				</Button>

				<Button
					primary
					loading={loading}
					disabled={files.length === 0 || loading || error}
					onClick={onClickUpload}
				>
					{t("upload", "Upload")}
				</Button>
			</Modal.Actions>
		</Modal>
	);
}
